import React, { useState } from 'react';
import { ArrowLeftIcon, BanknoteIcon, CheckCircle2Icon, DownloadIcon, EyeIcon, LockIcon, PencilIcon, SendIcon, UserPlusIcon, WrenchIcon } from 'lucide-react';
import { CompanySettings, Employee, InvoiceLine, InvoicePayment, PaymentTerms, ProCustomer, ProInvoice, ProJob, Product, TimeEntry } from '../../types/pro';
import { INVOICE_STATUS, formatDate, krPrecise } from '../../utils/pro';
import { PAYMENT_TERMS, dueDateFor, emptyInvoiceLine, invoiceAmounts, isEditable } from '../../utils/invoice';
import { StatusChip } from '../../components/pro/StatusChip';
import { contactsOf, primaryContact } from '../../utils/contacts';
import { CustomerPicker } from '../../components/pro/invoice/CustomerPicker';
import { ImportFromJobDialog } from '../../components/pro/invoice/ImportFromJobDialog';
import { InvoiceLines } from '../../components/pro/invoice/InvoiceLines';
import { InvoiceDocument } from '../../components/pro/invoice/InvoiceDocument';
import { ApproveDialog, PaymentDialog, SendDialog } from '../../components/pro/invoice/InvoiceDialogs';
interface ProInvoiceEditorProps {
  invoice: ProInvoice;
  customers: ProCustomer[];
  jobs: ProJob[];
  products: Product[];
  employees: Employee[];
  timeEntries: TimeEntry[];
  company: CompanySettings;
  onBack: () => void;
  onChange: (changes: Partial<ProInvoice>) => void;
  onCreateCustomer: () => void;
}
export function ProInvoiceEditor({
  invoice,
  customers,
  jobs,
  products,
  employees,
  timeEntries,
  company,
  onBack,
  onChange,
  onCreateCustomer
}: ProInvoiceEditorProps) {
  const [preview, setPreview] = useState(false);
  const [pickingCustomer, setPickingCustomer] = useState(false);
  const [importing, setImporting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [sending, setSending] = useState(false);
  const [paying, setPaying] = useState(false);
  const [saved, setSaved] = useState(false);
  const editable = isEditable(invoice);
  const sums = invoiceAmounts(invoice);
  const customer = customers.find(item => item.id === invoice.customerId);
  const customerJobs = jobs.filter(job => job.customerId && job.customerId === invoice.customerId);
  const job = jobs.find(item => item.id === invoice.jobId);
  const setBilling = (changes: Partial<ProInvoice['billing']>) => onChange({
    billing: {
      ...invoice.billing,
      ...changes
    }
  });

  /** Recomputes the due date whenever the date or terms move. */
  const applyTerms = (terms: PaymentTerms, days: number, invoiceDate: string) => {
    const due = dueDateFor(invoiceDate, terms, days);
    onChange({
      paymentTerms: terms,
      paymentTermsDays: days,
      invoiceDate,
      ...(due ? {
        dueDate: due
      } : {})
    });
  };
  const pickCustomer = (picked: ProCustomer) => {
    // A customer can have several people; the primary one is who the
    // invoice is addressed to unless it gets changed by hand afterwards.
    const lead = primaryContact(picked);
    onChange({
      customerId: picked.id ?? '',
      customerName: picked.name,
      billing: {
        name: picked.name,
        attention: lead && lead.name !== picked.name ? lead.name : '',
        address: picked.address,
        postalCode: picked.postalCode,
        city: picked.city,
        email: lead?.email || picked.email,
        phone: lead?.phone || picked.phone,
        cvr: picked.cvr ?? ''
      },
      // A job from a different customer would no longer make sense.
      jobId: undefined
    });
  };
  const contactOptions = customer ? contactsOf(customer) : [];
  const addImported = (lines: InvoiceLine[]) => onChange({
    lines: [...invoice.lines, ...lines]
  });
  const registerPayment = (payment: InvoicePayment) => {
    const payments = [...invoice.payments, payment];
    const paid = payments.reduce((sum, item) => sum + item.amount, 0);
    onChange({
      payments,
      status: paid + 0.01 >= sums.total ? 'betalt' : invoice.status
    });
  };
  const markSaved = () => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1800);
  };
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500 disabled:bg-stone-50 disabled:text-stone-500';
  const label = 'mb-1.5 block text-sm font-medium text-stone-700';
  const card = 'rounded-xl border border-stone-200 bg-white';
  const ghostButton = 'inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50';
  if (preview) {
    return <div data-id="element-1389" data-mp="1sfpxzr20moo8">
        <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-1390" data-mp="1wf76yugni3y5">
          <button onClick={() => setPreview(false)} className="inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-1391" data-mp="1wep3pdvkmpgq">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-1392" data-mp="26dnz3urbsols" />
            Tilbage til redigering
          </button>
          <span className="flex-1" data-id="element-1393" data-mp="2gimlqep3g2br" />
          <button onClick={() => window.print()} className={ghostButton} data-id="element-1394" data-mp="2bii06a9bwheo">
            <DownloadIcon className="h-4 w-4" data-id="element-1395" data-mp="2bkl1vrjufyz8" />
            Download PDF
          </button>
        </div>
        <InvoiceDocument invoice={invoice} company={company} data-id="element-1396" data-mp="26kympckgg8ns" />
      </div>;
  }
  return <div data-id="element-1397" data-mp="22jwn2ummlula">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-1398" data-mp="2z7t6065jd1zo">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-1399" data-mp="2uaszxv8orz8k" />
        Alle fakturaer
      </button>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-4" data-id="element-1400" data-mp="2pggfpizdyjvr">
        <div data-id="element-1401" data-mp="2lfcwqnm1k7qb">
          <div className="flex items-center gap-2.5" data-id="element-1402" data-mp="2lfwhec32ta81">
            <p className="font-mono text-xs font-semibold text-brand-700" data-id="element-1403" data-mp="3hyu3epr0cph4">
              {invoice.invoiceNo}
            </p>
            <StatusChip meta={INVOICE_STATUS[invoice.status]} data-id="element-1404" data-mp="3d6j2bjkj6xmz" />
            {saved && <span className="text-xs font-medium text-emerald-600" data-id="element-1405" data-mp="1b9s6k6ncw84h">
                Gemt
              </span>}
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl" data-id="element-1406" data-mp="1g8d8cai9azv1">
            {invoice.creditNoteOf ? 'Kreditnota' : 'Faktura'}
          </h1>
        </div>

        <div className="flex flex-wrap gap-2" data-id="element-1407" data-mp="16hqx0shkovi5">
          <button onClick={() => setPreview(true)} className={ghostButton} data-id="element-1408" data-mp="sjtwxm3wu6yq">
            <EyeIcon className="h-4 w-4" data-id="element-1409" data-mp="slwwawqydcg4" />
            Forhåndsvis
          </button>

          {editable && <>
              <button onClick={markSaved} className={ghostButton} data-id="element-1410" data-mp="ilw8oym1wa64">
                <CheckCircle2Icon className="h-4 w-4" data-id="element-1411" data-mp="inqvkigvuccu" />
                Gem kladde
              </button>
              <button onClick={() => setApproving(true)} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1412" data-mp="nmahiivh7o57">
                <LockIcon className="h-4 w-4" data-id="element-1413" data-mp="6bwupucy0cnf" />
                Godkend faktura
              </button>
            </>}

          {invoice.status === 'godkendt' && <button onClick={() => setSending(true)} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1414" data-mp="g181398g337m">
              <SendIcon className="h-4 w-4" data-id="element-1415" data-mp="ozti2zy9qa6a" />
              Send faktura
            </button>}

          {['sendt', 'forfalden'].includes(invoice.status) && <button onClick={() => setPaying(true)} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700" data-id="element-1416" data-mp="yyra4cuwklu1">
              <BanknoteIcon className="h-4 w-4" data-id="element-1417" data-mp="30t0cyq8pxiej" />
              Registrer betaling
            </button>}
        </div>
      </header>

      {!editable && <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4" data-id="element-1418" data-mp="29ydq4fs1dvwe">
          <LockIcon className="h-4 w-4 flex-shrink-0 text-indigo-600" data-id="element-1419" data-mp="2evx37cd002mr" />
          <p className="min-w-0 flex-1 text-sm text-indigo-900" data-id="element-1420" data-mp="2jq1bq01wwipy">
            Fakturaen er godkendt{' '}
            {invoice.approvedAt && `den ${formatDate(invoice.approvedAt)}`} og
            kan ikke længere rettes. Skal noget ændres, laver du en kreditnota.
          </p>
          <button onClick={() => onChange({
        status: 'kladde',
        approvedAt: undefined
      })} className="inline-flex items-center gap-2 rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100" data-id="element-1421" data-mp="1vycgobxphg5b">
            <PencilIcon className="h-3.5 w-3.5" data-id="element-1422" data-mp="3ce6x8p0sibqu" />
            Åbn som kladde igen
          </button>
        </div>}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-id="element-1423" data-mp="3hb5uhgfr2yt9">
        <div className="space-y-5 lg:col-span-2" data-id="element-1424" data-mp="15pn3nmi7x8vm">
          {/* 1 — who it goes to */}
          <section className={`${card} p-5`} data-id="element-1425" data-mp="10qaid66zw9v5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-1426" data-mp="10qk94vzv6l1w">
              <h2 className="text-base font-semibold text-stone-900" data-id="element-1427" data-mp="vyacyy85oor6">Kunde</h2>
              {editable && <button onClick={() => setPickingCustomer(true)} className={ghostButton} data-id="element-1428" data-mp="qzfrfoit0m3d">
                  <UserPlusIcon className="h-4 w-4" data-id="element-1429" data-mp="hzkdjbxv6czn" />
                  {customer ? 'Skift kunde' : 'Vælg kunde'}
                </button>}
            </div>

            {!customer && !invoice.billing.name ? <button onClick={() => setPickingCustomer(true)} disabled={!editable} className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-stone-300 px-4 py-8 text-sm font-semibold text-stone-500 transition-colors hover:border-brand-400 hover:bg-brand-50 hover:text-brand-700 disabled:hover:border-stone-300 disabled:hover:bg-transparent" data-id="element-1430" data-mp="81wlwuvk1llp">
                <UserPlusIcon className="h-4 w-4" data-id="element-1431" data-mp="2aqqtumer0stw" />
                Vælg kunde
              </button> : <>
                <p className="mb-3 text-xs text-stone-400" data-id="element-1432" data-mp="2kpg1lmcxkwwf">
                  Rettelser her gælder kun denne faktura — kundekartoteket
                  ændres ikke.
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-1433" data-mp="1s0hgqhhfbgnf">
                  <label className="block sm:col-span-2" data-id="element-1434" data-mp="1s2c0vtkvf4qg">
                    <span className={label} data-id="element-1435" data-mp="25s5q4d0afr0p">Navn</span>
                    <input value={invoice.billing.name} disabled={!editable} onChange={e => setBilling({
                  name: e.target.value
                })} className={field} data-id="element-1436" data-mp="25tqmm865xk17" />
                  </label>
                  <label className="block" data-id="element-1437" data-mp="199remuzofxvm">
                    <span className={label} data-id="element-1438" data-mp="z5jctwu23s4l">Att.</span>
                    <input value={invoice.billing.attention} disabled={!editable} onChange={e => setBilling({
                  attention: e.target.value
                })} className={field} data-id="element-1439" data-mp="z4rnv4uyssna" />
                    {/* Several people can belong to one customer — pick who
                        this particular invoice is addressed to. */}
                    {editable && contactOptions.length > 1 && <span className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-1440" data-mp="17yehaobp9jtb">
                        {contactOptions.map(contact => <button key={contact.id} type="button" onClick={() => setBilling({
                    attention: contact.name,
                    email: contact.email || invoice.billing.email,
                    phone: contact.phone || invoice.billing.phone
                  })} className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${invoice.billing.attention === contact.name ? 'bg-brand-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1441" data-mp="1cwg2pfwllv7y">
                            {contact.name}
                          </button>)}
                      </span>}
                  </label>
                  <label className="block" data-id="element-1442" data-mp="16jbw64g7y0kn">
                    <span className={label} data-id="element-1443" data-mp="35ulcenp3enkx">CVR</span>
                    <input value={invoice.billing.cvr} disabled={!editable} onChange={e => setBilling({
                  cvr: e.target.value
                })} className={field} data-id="element-1444" data-mp="35s8ugajc4d7v" />
                  </label>
                  <label className="block sm:col-span-2" data-id="element-1445" data-mp="3dc1d6w3fdrzc">
                    <span className={label} data-id="element-1446" data-mp="3i4aytzxic5vt">Adresse</span>
                    <input value={invoice.billing.address} disabled={!editable} onChange={e => setBilling({
                  address: e.target.value
                })} className={field} data-id="element-1447" data-mp="3i6nnsx1ongid" />
                  </label>
                  <label className="block" data-id="element-1448" data-mp="2uh2tji02wwts">
                    <span className={label} data-id="element-1449" data-mp="2zdjtd9gn4zmp">Postnummer</span>
                    <input value={invoice.billing.postalCode} disabled={!editable} onChange={e => setBilling({
                  postalCode: e.target.value
                })} className={field} data-id="element-1450" data-mp="2zg5xt088iz0p" />
                  </label>
                  <label className="block" data-id="element-1451" data-mp="h7iflondvrto">
                    <span className={label} data-id="element-1452" data-mp="hacyhbqr14kz">By</span>
                    <input value={invoice.billing.city} disabled={!editable} onChange={e => setBilling({
                  city: e.target.value
                })} className={field} data-id="element-1453" data-mp="d7gog9f9ygsn" />
                  </label>
                  <label className="block" data-id="element-1454" data-mp="106ml4nfpv3gl">
                    <span className={label} data-id="element-1455" data-mp="105la2iypagyv">E-mail</span>
                    <input value={invoice.billing.email} disabled={!editable} onChange={e => setBilling({
                  email: e.target.value
                })} className={field} data-id="element-1456" data-mp="w62qbqim8bxm" />
                  </label>
                  <label className="block" data-id="element-1457" data-mp="1juvntrevyruc">
                    <span className={label} data-id="element-1458" data-mp="1jvwxbrg9769y">Telefon</span>
                    <input value={invoice.billing.phone} disabled={!editable} onChange={e => setBilling({
                  phone: e.target.value
                })} className={field} data-id="element-1459" data-mp="1ewanlrzmw85w" />
                  </label>
                </div>
              </>}
          </section>

          {/* 2 — where the work came from */}
          {invoice.customerId && <section className={`${card} p-5`} data-id="element-1460" data-mp="1thg784c56cb1">
              <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-1461" data-mp="1etuz1w0etmw9">
                Knyt til opgave
              </h2>
              <div className="flex flex-wrap items-end gap-3" data-id="element-1462" data-mp="1jkthp28vex2o">
                <label className="block min-w-[220px] flex-1" data-id="element-1463" data-mp="15t59ha1qssqz">
                  <span className={label} data-id="element-1464" data-mp="15uq4t1vvsfpw">Opgave</span>
                  <select value={invoice.jobId ?? ''} disabled={!editable} onChange={e => onChange({
                jobId: e.target.value || undefined
              })} className={field} data-id="element-1465" data-mp="1arq9p9gzvcff">
                    <option value="" data-id="element-1466" data-mp="hpyz98stvq2i">Ingen opgave</option>
                    {customerJobs.map(item => <option key={item.id} value={item.id} data-id="element-1467" data-mp="cygz2mymptil">
                        {item.jobNo} — {item.title}
                      </option>)}
                  </select>
                </label>
                <button onClick={() => setImporting(true)} disabled={!editable || !job} className="inline-flex items-center gap-2 rounded-lg bg-stone-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-stone-800 disabled:bg-stone-200 disabled:text-stone-400" data-id="element-1468" data-mp="31volm3ubq7d">
                  <WrenchIcon className="h-4 w-4" data-id="element-1469" data-mp="lraq1hnfuhca" />
                  Hent fra opgave
                </button>
              </div>
              {customerJobs.length === 0 && <p className="mt-3 text-sm text-stone-500" data-id="element-1470" data-mp="3be9sd4rz04q6">
                  Denne kunde har ingen opgaver endnu.
                </p>}
            </section>}

          {/* 3 — the paperwork */}
          <section className={`${card} p-5`} data-id="element-1471" data-mp="1qybp7jsoaeyw">
            <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-1472" data-mp="1qz3keticyaon">
              Fakturaoplysninger
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1473" data-mp="1vvbtbp5yp7nd">
              <label className="block" data-id="element-1474" data-mp="20ppru0fx7uw9">
                <span className={label} data-id="element-1475" data-mp="25n815rxuoc4s">Fakturanummer</span>
                <input value={invoice.invoiceNo} disabled={!editable} onChange={e => onChange({
                invoiceNo: e.target.value
              })} className={`${field} font-mono`} data-id="element-1476" data-mp="25moooqqyyfy5" />
              </label>
              <label className="block" data-id="element-1477" data-mp="2jly0le856nwb">
                <span className={label} data-id="element-1478" data-mp="2jkwnz5bh9tch">Fakturadato</span>
                <input type="date" value={invoice.invoiceDate} disabled={!editable} onChange={e => applyTerms(invoice.paymentTerms, invoice.paymentTermsDays, e.target.value)} className={field} data-id="element-1479" data-mp="kawyeq74nc8t" />
              </label>
              <label className="block" data-id="element-1480" data-mp="30y5yyqg24f5i">
                <span className={label} data-id="element-1481" data-mp="2vwyup1d4hy3l">Betalingsbetingelser</span>
                <select value={invoice.paymentTerms} disabled={!editable} onChange={e => applyTerms(e.target.value as PaymentTerms, invoice.paymentTermsDays, invoice.invoiceDate)} className={field} data-id="element-1482" data-mp="287o1c4y3r5zh">
                  {PAYMENT_TERMS.map(option => <option key={option.id} value={option.id} data-id="element-1483" data-mp="3tj5ywa5iloff">
                      {option.label}
                    </option>)}
                </select>
              </label>

              {['netto', 'loebende-maaned'].includes(invoice.paymentTerms) && <label className="block" data-id="element-1484" data-mp="4zx22gofvew9">
                  <span className={label} data-id="element-1485" data-mp="3n4qkamonpzv7">Antal dage</span>
                  <input type="number" min={0} value={invoice.paymentTermsDays} disabled={!editable} onChange={e => applyTerms(invoice.paymentTerms, parseInt(e.target.value, 10) || 0, invoice.invoiceDate)} className={`${field} tabular-nums`} data-id="element-1486" data-mp="3n6bf8devvktl" />
                </label>}

              <label className="block" data-id="element-1487" data-mp="11ouwtyw6vctf">
                <span className={label} data-id="element-1488" data-mp="11ntow3agz6fx">Forfaldsdato</span>
                <input type="date" value={invoice.dueDate} disabled={!editable} onChange={e => onChange({
                dueDate: e.target.value
              })} className={field} data-id="element-1489" data-mp="16gmsuoy46aqy" />
              </label>
              <label className="block" data-id="element-1490" data-mp="2obnuwydculua">
                <span className={label} data-id="element-1491" data-mp="2oamiapgoxrag">Reference</span>
                <input value={invoice.reference} disabled={!editable} onChange={e => onChange({
                reference: e.target.value
              })} placeholder="Kundens kontaktperson" className={field} data-id="element-1492" data-mp="2ta7it4bie7pa" />
              </label>
              <label className="block" data-id="element-1493" data-mp="25d1sng63qoos">
                <span className={label} data-id="element-1494" data-mp="25e36ttiezraq">Ordrenummer</span>
                <input value={invoice.orderNo} disabled={!editable} onChange={e => onChange({
                orderNo: e.target.value
              })} className={field} data-id="element-1495" data-mp="2ab3ajxrskhyo" />
              </label>
            </div>
          </section>

          {/* 4 — the money */}
          <InvoiceLines lines={invoice.lines} products={products} pricesIncludeVat={invoice.pricesIncludeVat} readOnly={!editable} onChange={lines => onChange({
          lines
        })} onAddLine={() => onChange({
          lines: [...invoice.lines, emptyInvoiceLine(company.vatRate)]
        })} data-id="element-1496" data-mp="1t3c5u2trz4dd" />

          {/* 5 — what it says */}
          <section className={`${card} space-y-4 p-5`} data-id="element-1497" data-mp="2bumv6fjur935">
            <label className="block" data-id="element-1498" data-mp="27q5fargm5fxn">
              <span className={label} data-id="element-1499" data-mp="27t9rab0blfyv">Note til kunden</span>
              <textarea value={invoice.notes} disabled={!editable} onChange={e => onChange({
              notes: e.target.value
            })} rows={3} placeholder="Tak for ordren. Fakturaen vedrører…" className={`${field} leading-relaxed`} data-id="element-1500" data-mp="2lpn5nb9k5194" />
              <span className="mt-1 block text-xs text-stone-400" data-id="element-1501" data-mp="mfyjkgj35v66">
                Vises nederst på fakturaen.
              </span>
            </label>
            <label className="block" data-id="element-1502" data-mp="2reeqx8kcq0vg">
              <span className={label} data-id="element-1503" data-mp="2rfgc45v303qw">Intern note</span>
              <textarea value={invoice.internalNote} onChange={e => onChange({
              internalNote: e.target.value
            })} rows={2} placeholder="Kun for jer — kunden ser den aldrig." className={`${field} bg-amber-50/60 leading-relaxed`} data-id="element-1504" data-mp="2vgieuwo7ixxm" />
            </label>
          </section>
        </div>

        {/* Totals follow you down the page */}
        <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start" data-id="element-1505" data-mp="3f628v0zmy1ll">
          <div className={`${card} p-5`} data-id="element-1506" data-mp="3j6m604k7l57q">
            <h2 className="text-base font-semibold text-stone-900" data-id="element-1507" data-mp="3j5ksltfq06mu">Sum</h2>

            <fieldset className="mt-4" data-id="element-1508" data-mp="3o6s1xwxihdvm">
              <legend className="mb-1.5 text-xs font-medium uppercase tracking-wide text-stone-400" data-id="element-1509" data-mp="3o3mbad98i3xs">
                Indtast priser
              </legend>
              <div className="flex rounded-lg bg-stone-100 p-0.5" data-id="element-1510" data-mp="744eo6hyvswa">
                {[{
                value: false,
                label: 'Ekskl. moms'
              }, {
                value: true,
                label: 'Inkl. moms'
              }].map(option => <button key={option.label} onClick={() => editable && onChange({
                pricesIncludeVat: option.value
              })} disabled={!editable} aria-pressed={invoice.pricesIncludeVat === option.value} className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${invoice.pricesIncludeVat === option.value ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-1511" data-mp="3tcdqt12ve048">
                    {option.label}
                  </button>)}
              </div>
            </fieldset>

            <label className="mt-4 block" data-id="element-1512" data-mp="odot8hp6iqnh">
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-stone-400" data-id="element-1513" data-mp="taowylyk3hbf">
                Rabat på hele fakturaen (%)
              </span>
              <input type="number" min={0} max={100} value={invoice.totalDiscount} disabled={!editable} onChange={e => onChange({
              totalDiscount: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0))
            })} className={`${field} tabular-nums`} data-id="element-1514" data-mp="jc93icc23vyr" />
            </label>

            <dl className="mt-4 space-y-2 border-t border-stone-100 pt-4 text-sm" data-id="element-1515" data-mp="1pzmta8lyfnap">
              <div className="flex justify-between" data-id="element-1516" data-mp="1uro2flntsxwi">
                <dt className="text-stone-500" data-id="element-1517" data-mp="1utiq37qhf44a">Subtotal</dt>
                <dd className="font-medium tabular-nums text-stone-900" data-id="element-1518" data-mp="3fgrvn49bx3x1">
                  {krPrecise(sums.subtotal)}
                </dd>
              </div>
              {sums.invoiceDiscount > 0 && <div className="flex justify-between" data-id="element-1519" data-mp="3p6bdq7p7wtqu">
                  <dt className="text-stone-500" data-id="element-1520" data-mp="3k8jpporp1rrx">Rabat</dt>
                  <dd className="font-medium tabular-nums text-emerald-600" data-id="element-1521" data-mp="3k91oot5qqk3j">
                    −{krPrecise(sums.invoiceDiscount)}
                  </dd>
                </div>}
              <div className="flex justify-between" data-id="element-1522" data-mp="2rlfcwxmd095l">
                <dt className="text-stone-500" data-id="element-1523" data-mp="36gvdllhg33ay">Moms</dt>
                <dd className="font-medium tabular-nums text-stone-900" data-id="element-1524" data-mp="36ig5t4gaea5n">
                  {krPrecise(sums.vat)}
                </dd>
              </div>
            </dl>

            <div className="mt-4 border-t border-stone-200 pt-4" data-id="element-1525" data-mp="lpuvrav4eds7">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-400" data-id="element-1526" data-mp="gs32odiqolmf">
                Total inkl. moms
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-stone-900" data-id="element-1527" data-mp="nu7yq1nfmsxr">
                {krPrecise(sums.total)}
              </p>
            </div>

            {sums.paid > 0 && <dl className="mt-4 space-y-2 border-t border-stone-100 pt-4 text-sm" data-id="element-1528" data-mp="xu8ifop7rb2h">
                <div className="flex justify-between" data-id="element-1529" data-mp="12sjvs9epw3pg">
                  <dt className="text-stone-500" data-id="element-1530" data-mp="12pxped3kbu8t">Betalt</dt>
                  <dd className="font-medium tabular-nums text-emerald-600" data-id="element-1531" data-mp="55ukf8y4keqx">
                    {krPrecise(sums.paid)}
                  </dd>
                </div>
                <div className="flex justify-between font-semibold" data-id="element-1532" data-mp="f2z7dazsoe8s">
                  <dt className="text-stone-900" data-id="element-1533" data-mp="f0uwua7e4gzj">Rest</dt>
                  <dd className="tabular-nums text-stone-900" data-id="element-1534" data-mp="juh8q05qcgua">
                    {krPrecise(sums.outstanding)}
                  </dd>
                </div>
              </dl>}
          </div>

          {invoice.dueDate && <p className="px-1 text-xs text-stone-400" data-id="element-1535" data-mp="3ngepb708lvdj">
              Forfalder {formatDate(invoice.dueDate)}.
            </p>}
        </aside>
      </div>

      <CustomerPicker isOpen={pickingCustomer} onClose={() => setPickingCustomer(false)} customers={customers} onSelect={pickCustomer} onCreateNew={onCreateCustomer} data-id="element-1536" data-mp="20n4bcz29l0ez" />

      <ImportFromJobDialog isOpen={importing} onClose={() => setImporting(false)} job={job} timeEntries={timeEntries} employees={employees} fallbackRate={company.hourlyRate} vatRate={company.vatRate} onImport={addImported} data-id="element-1537" data-mp="1qndh8yhm8nff" />

      <ApproveDialog isOpen={approving} onClose={() => setApproving(false)} invoice={invoice} onApprove={() => onChange({
      status: 'godkendt',
      approvedAt: new Date().toISOString()
    })} data-id="element-1538" data-mp="1cwqfexgk6hf4" />

      <SendDialog isOpen={sending} onClose={() => setSending(false)} invoice={invoice} company={company} onSend={() => onChange({
      status: 'sendt',
      sentAt: new Date().toISOString()
    })} onOpenPreview={() => setPreview(true)} data-id="element-1539" data-mp="xzzgsqepl9qk" />

      <PaymentDialog isOpen={paying} onClose={() => setPaying(false)} invoice={invoice} onRegister={registerPayment} data-id="element-1540" data-mp="2ctwg70iowdrm" />
    </div>;
}